import React from 'react'

import styled from '@emotion/styled'
import { Color, colors, Theme, useTheme } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { trackUTMAndPromoCode } from 'actions/tracking'
import { checkoutClickContinueToDelivery, clickApplePayCheckout } from 'actions/trackingKeys'
import { userProspect } from 'actions/user'
import { useIsOptimizelyFeatureEnabled } from 'containers/OptimizelyRollouts'
import { EmailPasswordForm } from 'routes/Checkout/Components/AboutYou/EmailPasswordForm'
import { ErrorMessage } from 'routes/Checkout/Components/ErrorMessage/ErrorMessage'
import { SectionHeader } from 'routes/Checkout/Components/SectionHeader'
import { setCurrentPaymentMethod } from 'routes/Checkout/checkoutActions'
import { CheckoutStepIds } from 'routes/Checkout/checkoutConfig'
import { usePricing } from 'routes/Menu/domains/pricing'
import { PaymentMethod } from 'routes/Signup/signupConfig'

import { SecurePaymentBanner } from './SecurePaymentBanner'

type AboutYouProps = {
  onStepChange: (nextStepId: CheckoutStepIds) => void
}

type ThemeProp = {
  citrusTheme: Theme
}

const AboutYouContainer = styled.div(({ citrusTheme }: ThemeProp) => {
  const mackerelColor = '#D2D6D9'
  const BreakpointFromMedium = `@media (min-width: ${citrusTheme.tokens.breakpoints[0]})`

  return {
    borderBottom: `1px solid ${mackerelColor}`,
    borderTop: `1px solid ${mackerelColor}`,
    padding: '1rem 0.75rem 1.5rem',
    paddingBottom: '1rem',
    background: colors[Color.White],

    [BreakpointFromMedium]: {
      border: `1px solid ${mackerelColor}`,
      padding: '1.5rem 1.5rem 2rem',
    },
  }
})

export const AboutYou = ({ onStepChange }: AboutYouProps) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const { pricing } = usePricing()
  const isMoveDeliveryInstructionEnabled = useIsOptimizelyFeatureEnabled(
    'beetroots_move_delivery_instructions_form_web_enabled_v3',
  )

  const onSubmit = (
    email: string,
    password: string,
    isEmailMarketingEnabled: boolean,
    isApplePay?: boolean,
  ) => {
    dispatch({
      type: actionTypes.CHECKOUT_SET_EMAIL,
      email,
    })
    dispatch({ type: actionTypes.CHECKOUT_SET_IS_EMAIL_MARKETING_ENABLED, isEmailMarketingEnabled })

    if (isApplePay) {
      dispatch(trackUTMAndPromoCode(clickApplePayCheckout, { total: pricing?.total ?? '' }))
      dispatch(setCurrentPaymentMethod(PaymentMethod.ApplePay))
    } else {
      dispatch(trackUTMAndPromoCode(checkoutClickContinueToDelivery))
    }
    dispatch(userProspect())
    onStepChange(isApplePay ? CheckoutStepIds.APPLE_PAY : CheckoutStepIds.DELIVERY)
  }

  return (
    <>
      <AboutYouContainer citrusTheme={theme} data-testing="checkoutAboutYouSection">
        <SectionHeader title="Create account" />
        <EmailPasswordForm onSubmit={onSubmit} />
        <ErrorMessage />
      </AboutYouContainer>
      {isMoveDeliveryInstructionEnabled && <SecurePaymentBanner />}
    </>
  )
}
